import { useEffect, useRef, useCallback } from 'react';
import { useGameActions } from '../context/GameProvider';
import type { ComboEvent } from '../types';
import type { ActivityEntry } from '../components/ActivityFeed';

// Listens for game-specific events published via the custom event bus.
// The chat WebSocket forwards game messages to the bus via emitGameEvent().

const GAME_EVENT_KEY = 'opspilot:gameEvent';

export function emitGameEvent(type: string, payload: unknown): void {
  window.dispatchEvent(new CustomEvent(GAME_EVENT_KEY, { detail: { type, payload } }));
}

export function useGameEvents(onActivity?: (entry: ActivityEntry) => void): void {
  const { loadStatus, loadQuests, dispatchCombo } = useGameActions();
  const actionsRef = useRef({ loadStatus, loadQuests, dispatchCombo });
  const onActivityRef = useRef(onActivity);

  useEffect(() => {
    actionsRef.current = { loadStatus, loadQuests, dispatchCombo };
  }, [loadStatus, loadQuests, dispatchCombo]);

  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  const handleEvent = useCallback((e: Event) => {
    const { type, payload } = (e as CustomEvent<{ type: string; payload: unknown }>).detail;
    const actions = actionsRef.current;

    switch (type) {
      case 'questUpdate':
        actions.loadQuests();
        actions.loadStatus();
        break;
      case 'achievementUnlocked':
        actions.loadStatus();
        break;
      case 'comboTriggered':
        actions.dispatchCombo(payload as ComboEvent);
        break;
      case 'activity':
        onActivityRef.current?.(payload as ActivityEntry);
        break;
      case 'gameEvent':
        actions.loadStatus();
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener(GAME_EVENT_KEY, handleEvent);
    return () => window.removeEventListener(GAME_EVENT_KEY, handleEvent);
  }, [handleEvent]);
}
